import angular from 'angular';
import { isArray } from 'lodash-es';

import * as EquipmentServiceUtil from './equipment.service.util';

import { INITIAL_STATE } from '../state/search/search.state';
import { ASSET_TYPE_ATTRIBUTE } from '../api/assetTypeAttribute';

const getLastCommunicationOptions = () =>
  INITIAL_STATE.lastCommunicated.map((lscomm) => ({ ...lscomm, _selected: false }));

export default function EquipmentService(
  $q,
  $http,
  appSettings,
  $rootScope,
  EquipmentMockService,
  securityGroupsService,
  gettextCatalog,
  urlUtil,
  equipmentSearchService,
) {
  'ngInject';

  if (appSettings.FixturesModeEnabled) {
    return EquipmentMockService;
  }

  const toArray = (input) => {
    input = !input ? [] : input;
    return Array.isArray(input) ? input : [input];
  };

  const lowercaseKeys = (item) => {
    _.forEach(item, function (value, key) {
      let lower = key.toLowerCase();
      // check if it already wasn't lowercase
      if (lower !== key) {
        item[lower] = item[key];
        delete item[key];
      }
    });
    return item;
  };

  //keys for facets dictionary access
  let FACET_SECURITY_GROUPS_KEY = 'securityGroups';
  let FACET_MAINTENANCE_KEY = 'maintenance';
  let FACET_ALERT_PRIORITY_KEY = 'alerts';
  let FACET_LAST_COMM_KEY = 'lastCommunicated';

  let isDtcSeries = function (seriesItem) {
    let filtered = true;

    if (seriesItem.tags != null && seriesItem.tags.length > 0) {
      for (let i = 0; i < seriesItem.tags.length; i++) {
        if (seriesItem.tags[i] === 'dtc') {
          return true;
        }
      }
    }
    return !filtered;
  };

  return {
    //UTILS
    //Get the equipment priority based on the properties
    getEquipmentPriority: function (equipment) {
      let maxPriority = 0;

      if (equipment && equipment.properties) {
        for (let i = 0; i < equipment.properties.length; i++) {
          let tag = equipment.properties[i];

          if (tag.indexOf('alert:high') !== -1) {
            maxPriority = Math.max(3, maxPriority);
          } else if (tag.indexOf('alert:medium') !== -1) {
            maxPriority = Math.max(2, maxPriority);
          }
        }
      }
      return maxPriority;
    },
    //API
    getEquipmentWithinBounds: function (nw, se, q) {
      if (q)
        return $http.get(
          appSettings.InReachApiEquipmentEndpoint +
            '/boundingbox/' +
            nw.lng +
            '/' +
            nw.lat +
            '/' +
            se.lng +
            '/' +
            se.lat +
            '/' +
            q,
        );
      else
        return $http.get(
          appSettings.InReachApiEquipmentEndpoint +
            '/boundingbox/' +
            nw.lng +
            '/' +
            nw.lat +
            '/' +
            se.lng +
            '/' +
            se.lat +
            '/',
        );
    },
    getTimeseriesCsvData: (equipmentId, timeRange) =>
      $http.get(
        `${appSettings.InReachApiTimeSeriesEndpoint}/csv/${equipmentId}?timeRange=${timeRange}`,
      ),
    getEquipment: function (equipmentId, cancelDefer, includeList, uom) {
      let options = {};
      if (cancelDefer) {
        options['timeout'] = cancelDefer;
      }
      let endpoint = appSettings.InReachApiEquipmentEndpoint + '/';

      if (equipmentId) {
        endpoint += equipmentId;
      }

      let first = true;

      if (Array.isArray(includeList)) {
        for (let i = 0; i < includeList.length; i++) {
          endpoint += (first ? '?' : '&') + 'include=' + includeList[i];
          first = false;
        }
      }
      if (!uom) {
        uom = $rootScope.currentUser.claims.data.unitOfMeasure;
      }

      if (uom) {
        endpoint += (first ? '?' : '&') + 'uom=' + uom;
      }

      return $http.get(endpoint, options);
    },

    getEquipmentActiveFaults: function (seriesList) {
      let results = [];

      for (let i = 0; i < seriesList.length; i++) {
        if (isDtcSeries(seriesList[i])) {
          if (seriesList[i].last && seriesList[i].last.v !== 0) {
            results.push(seriesList[i]);
          }
        }
      }
      return results;
    },

    getDtcFaultDescriptor: (fault = {}) =>
      EquipmentServiceUtil.findFaultDtcDescriptor(fault, $rootScope.datapointDescriptorsByKey),
    formatFaultCode: (fault = {}) => EquipmentServiceUtil.formatFaultCode(fault),
    getEquipmentSeries: function (equipmentId, tag) {
      let uom = $rootScope.currentUser.claims.data.unitOfMeasure;
      return $http.get(
        appSettings.InReachApiTimeSeriesEndpoint +
          '/?equipmentId=' +
          equipmentId +
          (tag ? '&tag=' + tag : '') +
          '&uom=' +
          uom,
      );
    },
    getSeriesByMultipleEquipment: function (equipmentIds) {
      if (equipmentIds.length === 0) return;
      let url = appSettings.InReachApiTimeSeriesEndpoint + '/filter';
      return $http.post(url, equipmentIds);
    },
    getEquipSeriesData: function (seriesIDs, range, interval, functions) {
      let url = appSettings.InReachApiTimeSeriesEndpoint + '/data?';

      let requestData = {
        ids: seriesIDs,
        uom: $rootScope.currentUser.claims.data.unitOfMeasure,
        interval: angular.isString(interval) ? interval : null,
        functions: Array.isArray(functions) ? functions : null,
        range:
          Array.isArray(range) && range.length === 2
            ? urlUtil.toRangeParam(range[0], range[1])
            : '1point',
      };

      return $http.post(url, requestData);
    },

    //Return a list of all the things we can facetted search on
    getEquipmentFacets: function () {
      let def = $q.defer();
      let result = {};

      function onError(response) {
        def.reject(response);
      }

      let alerts = [];
      alerts.push({
        name: gettextCatalog.getString('Low'),
        description: gettextCatalog.getString('Assets with active low priority alerts'),
        tag: 'onei3:alert:low',
        icon: 'fa fa-exclamation-circle color-low-priority',
      });
      alerts.push({
        name: gettextCatalog.getString('Medium'),
        tag: 'onei3:alert:medium',
        icon: 'fa fa-exclamation-circle color-medium-priority',
        description: gettextCatalog.getString('Assets with active medium priority alerts'),
      });
      alerts.push({
        name: gettextCatalog.getString('High'),
        tag: 'onei3:alert:high',
        description: gettextCatalog.getString('Assets with active high priority alerts'),
        icon: 'fa fa-exclamation-circle color-high-priority',
      });

      result[FACET_ALERT_PRIORITY_KEY] = alerts;

      let maintenance = [];

      maintenance.push({
        name: gettextCatalog.getString('Due'),
        tag: 'onei3:maintenance:due',
        icon: 'fa fa-wrench color-high-priority',
        description: gettextCatalog.getString('Assets with maintenance due'),
      });

      maintenance.push({
        name: gettextCatalog.getString('Upcoming'),
        tag: 'onei3:maintenance:upcoming',
        description: gettextCatalog.getString('Assets with maintenance upcoming'),
        icon: 'fa fa-wrench color-low-priority',
      });

      result[FACET_MAINTENANCE_KEY] = maintenance;

      //using state last communication options
      result[FACET_LAST_COMM_KEY] = getLastCommunicationOptions();

      securityGroupsService.getAllSecurityGroups().then(function (response) {
        let securityGroups = response.data.data;
        result[FACET_SECURITY_GROUPS_KEY] = [];
        let secGroupPrefix = 'onei3:security:group:';
        //Need to append the quantity from the tags
        for (let i = 0; i < securityGroups.length; i++) {
          let group = securityGroups[i];

          //create the facet object for securty groups
          result[FACET_SECURITY_GROUPS_KEY].push({
            name: group.name,
            tag: secGroupPrefix + group.id,
            description: group.attrs.description || group.name,
          });
        }

        def.resolve(result);
      }, onError);

      return def.promise;
    },

    getEquipmentSearch: function (
      query,
      options,
      filters,
      bounds,
      shareKey,
      facets,
      tagFilters,
      modes,
      includes,
      prefilter,
      sort,
      accounts,
    ) {
      let share = shareKey
        ? {
            to: shareKey,
          }
        : null;

      if (!query) {
        query = [];
      }

      query = toArray(query);

      let filter = equipmentSearchService.buildAPIEquipmentFilterObject(
        query,
        options,
        bounds,
        share,
        facets,
        tagFilters,
        modes,
        prefilter,
        sort,
        accounts,
      );
      return this.getEquipmentBySearchFilter(filter).then(function (response) {
        if (
          filter.searchOutsideBound &&
          isArray(response.data.hits) &&
          response.data.hits.length > 0
        ) {
          response.data.locationRedirectFlag = true;
        }
        return response;
      });
    },
    getAllEquipmentSearch: (
      query,
      options,
      filters,
      bounds,
      shareKey,
      facets,
      tagFilters,
      modes,
      includes,
      prefilter,
      sort,
    ) => {
      let share = shareKey
        ? {
            to: shareKey,
          }
        : null;

      query = toArray(query);

      let filter = equipmentSearchService.buildAPIEquipmentFilterObject(
        query,
        { limit: 2500 },
        bounds,
        share,
        facets,
        tagFilters,
        modes,
        prefilter,
        sort,
      );

      let endpoint = appSettings.InReachApiEquipmentEndpoint + '/all/filter';
      return $http.post(endpoint, filter).then(function (response) {
        if (
          filter.searchOutsideBound &&
          isArray(response.data.hits) &&
          response.data.hits.length > 0
        ) {
          response.data.locationRedirectFlag = true;
        }
        return response;
      });
    },
    putEquipment: function (equipment) {
      let url = appSettings.InReachApiEndpoint + '/equipment/' + equipment.id;
      return $http.put(url, equipment);
    },
    batchPatchAttrs: function (changeset) {
      let url = appSettings.InReachApiEndpoint + '/equipment/batchPatch';
      return $http.patch(url, changeset);
    },
    getSingleEquipment: function (equipId) {
      let endpoint = appSettings.InReachApiEndpoint + '/equipment/' + equipId;
      return $http.get(endpoint);
    },
    postEquipmentListByIds: function (equipIds) {
      let endpoint = appSettings.IcebergApiEquipmentEndpoint + '/equipment/_filter';
      return $http.post(endpoint, equipIds).then(function (response) {
        response.data.forEach((equipment) => {
          equipment.attrs = lowercaseKeys(equipment.attrs);
        });
        return response;
      });
    },
    getAllTags: function () {
      let endpoint = appSettings.IcebergApiAttributesEndpoint + '/attrs';
      return $http.get(endpoint).then((response) => {
        // Adding the asset type attr.
        //response.data.data.push(ASSET_TYPE_ATTRIBUTE);
        return response;
      });
    },
    getTagById: function (id) {
      let endpoint = appSettings.IcebergApiAttributesEndpoint + '/attrs/' + id;
      return $http.get(endpoint);
    },
    getTagNames: function () {
      let endpoint = appSettings.IcebergApiAttributesEndpoint + '/attrs/attr/names';
      return $http.get(endpoint);
    },
    postNewTag: function (tag) {
      let endpoint = appSettings.IcebergApiAttributesEndpoint + '/attrs';
      return $http.post(endpoint, tag);
    },
    putEditTag: function (id, tag) {
      let endpoint = appSettings.IcebergApiAttributesEndpoint + '/attrs/' + id;
      return $http.put(endpoint, tag);
    },
    deleteTag: function (id) {
      let endpoint = appSettings.IcebergApiAttributesEndpoint + '/attrs/' + id;
      return $http.delete(endpoint);
    },
    patchEquipmentProfileImage: function (equipId, image) {
      let endpoint =
        appSettings.InReachApiEquipmentEndpoint + '/' + equipId + '/relationships/profileimage';
      let body = {};
      body.data = {};
      body.data.id = image.id;
      return $http.patch(endpoint, body);
    },

    getEquipmentBySearchFilter: function (filter) {
      if (!filter) {
        filter = {
          queries: ['*'],
        };
      }
      let endpoint = appSettings.InReachApiEquipmentEndpoint + '/search/filter';
      return $http.post(endpoint, filter);
    },
  };
}
